// ===== ./src/render/renderVideoContent.ts =====
//
// Responsibilities (refactored):
//   • Render standard (non-reel) video backgrounds inside a section.
//   • Accepts `video-reel` + `reel-layout-h` as aliases of `video-v` + `video-layout-h`.
//   • Defer ALL geometry/blur decisions to utils/layoutVideo.ts (+ videoCanvas.ts).
//   • Ensure still backgrounds (background-h / background-v) are applied behind video.
//   • Create a stable .sc-stage + .sc-content stack and move .container inside it.
//
// Rules for video backgrounds (unchanged behaviorally):
//   • Video backgrounds NEVER use Ken Burns / slide animations.
//   • If `background-h` is provided → use as a landscape still image behind video.
//   • If `background-v` is provided → use as a portrait still image behind video.
//   • If `video-v` and `video-h` are both missing → render a "missing media" placeholder.
//
// Notes:
//   • Geometry is now owned by utils/layoutVideo.applyVideoLayout.
//   • Canvas creation/sizing is owned by render/videoCanvas.setupVideoCanvas (called by applyVideoLayout).
//   • This file focuses on element creation, background setup, stage stack, and simple flags.
//
// Imports
import { log } from "../utils/debug";
import { parseInlineAttrs } from "../markdown/parseInlineAttrs";
import { renderMissingMedia } from "./fallback";
import { videoZ, applyVideoLayout } from "../utils/layoutVideo";
import { resolveAssetSrc, resolveVideoSrc } from "../utils/resolveAssets";
import { applySectionBackground } from "../utils/backgroundUtils";
import { isColor } from "../utils/color";
import { overlayClassMap } from "../utils/overlay";
import { mountReelHUD } from "../elements/hudReel/hudReel";
import { mountCaptionsElement } from "../elements/captionsElement";
import { getCdnStatus, markCdnFailure } from "../utils/cdnFallback";
import { instrumentReelVideo } from "../utils/clarityAnalytics";

// ---- Debug helpers ----
function shouldDebug(attrs: Record<string, any>): boolean {
  try {
    const url = new URL(window.location.href);
    const qp = url.searchParams.get("scDebug");
    const ls = (window.localStorage && window.localStorage.getItem("storycoreDebug")) || "";
    const attrFlag =
      attrs && (attrs["debug"] === true || String(attrs["debug"]).toLowerCase() === "true"
             || attrs["video-debug"] === true || String(attrs["video-debug"]).toLowerCase() === "true");
    const isDev = (typeof process !== "undefined" && (process as any).env && (process as any).env.NODE_ENV !== "production");
    return qp === "1" || ls === "1" || attrFlag || isDev;
  } catch {
    return false; // don’t spam logs if something explodes here
  }
}
function dbg(enabled: boolean, ...args: any[]) {
  if (enabled) log("[renderVideoContent]", ...args);
}
function extractSrcToken(val: string | undefined): string {
  if (!val) return "";
  const first = val.split(",")[0].trim();
  return first.replace(/\s+\w+\s*:\s*.*$/, "");
}

function boolishMeta(name: string): boolean | null {
  try {
    const value = document.querySelector(`meta[name="${name}"]`)?.getAttribute("content");
    if (value == null) return null;
    const normalized = String(value).trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    if (["0", "false", "no", "off"].includes(normalized)) return false;
  } catch {}
  return null;
}

function shouldAllowLocalMediaFallback(primaryUrl: string, fallbackUrl: string | null): boolean {
  if (!fallbackUrl || fallbackUrl === primaryUrl) return false;

  const explicitWindow = typeof window !== "undefined" ? (window as any).__STORYCORE_LOCAL_MEDIA_FALLBACK__ : undefined;
  if (typeof explicitWindow === "boolean") return explicitWindow;

  const explicitMeta = boolishMeta("storycore-local-media-fallback");
  if (explicitMeta !== null) return explicitMeta;

  try {
    const primary = new URL(primaryUrl, window.location.href);
    const fallback = new URL(fallbackUrl, window.location.href);
    return primary.origin === fallback.origin;
  } catch {
    return false;
  }
}

// Public API
export function renderVideoContent(
  section: HTMLElement,
  attrs: Record<string, any>,
  baseUrl: string,
  _isFirst: boolean = false
): HTMLVideoElement | null {
  const DEBUG = shouldDebug(attrs);

  // --- Audit/Lighthouse mode: skip heavy video for non-first sections ---
  const AUDIT_MODE = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.has("audit") || p.has("nogate");
    } catch {
      return false;
    }
  })();

  // Alias + normalization: treat `video-reel` as an authoring alias of `video-v`,
  // and map `reel-layout-h` to `video-layout-h`. Keep an `is-reel` hint for HUDs.
  const effAttrs: Record<string, any> = { ...attrs };
  if (effAttrs["video-reel"]) {
    effAttrs["is-reel"] = "1";
    if (!effAttrs["video-v"] && !effAttrs["video-h"]) {
      effAttrs["video-v"] = effAttrs["video-reel"];
    }
    if (effAttrs["reel-layout-h"] && !effAttrs["video-layout-h"]) {
      effAttrs["video-layout-h"] = effAttrs["reel-layout-h"];
    }
  }

  // 1) Resolve which video source to use (vertical preferred, else horizontal)
  let videoSource = "";
  if (effAttrs["video-v"]) {
    videoSource = extractSrcToken(effAttrs["video-v"]);
  } else if (effAttrs["video-h"]) {
    videoSource = extractSrcToken(effAttrs["video-h"]);
  } else {
    renderMissingMedia(section, "video");
    return null;
  }

  const rawInline = (effAttrs["video-v"] || effAttrs["video-h"] || "") as string;
  if (/\s+\w+\s*:\s*/.test(rawInline) && rawInline.includes(videoSource) && rawInline.trim() !== videoSource) {
    dbg(DEBUG, "Sanitized video src token from inline value:", { rawInline, sanitized: videoSource });
  }

  // 2) Apply section background (solid color or still image); also store color var
  section.style.setProperty("--section-bg", isColor(effAttrs["background-color"] || "") ? String(effAttrs["background-color"]) : "transparent");
  applySectionBackground(section, effAttrs, baseUrl);

  // 3) Ensure a stable stage wrapper for layering
  let stage = section.querySelector(':scope > .sc-stage') as HTMLDivElement | null;
  if (!stage) {
    stage = document.createElement("div");
    stage.className = "sc-stage";
    stage.style.position = "absolute";
    (stage.style as any).inset = "0";
    stage.style.left = "0";
    stage.style.top = "0";
    stage.style.width = "100%";
    stage.style.height = "100%";
    section.appendChild(stage);
  } else {
    stage.style.position = "absolute";
    (stage.style as any).inset = "0";
    stage.style.left = "0";
    stage.style.top = "0";
    stage.style.width = "100%";
    stage.style.height = "100%";
  }
  section.dataset.hasVideoBg = "1";
  const stageEl: HTMLDivElement = stage;

  // Reel/video frame overlay (used to mount HUD exactly over the video window)
  let reelFrame = stageEl.querySelector(':scope > .reel-frame') as HTMLDivElement | null;
  if (!reelFrame) {
    reelFrame = document.createElement('div');
    reelFrame.className = 'reel-frame';
    Object.assign(reelFrame.style, {
      position: 'absolute',
      left: '0px',
      top: '0px',
      width: '0px',
      height: '0px',
      pointerEvents: 'none', // HUD children flip to auto
      zIndex: String(videoZ('hud')),
    } as Partial<CSSStyleDeclaration>);
    stageEl.appendChild(reelFrame);
  }

  // 4) Create the <video> element (minimal baseline styles; layout owns sizing/position)
  const primaryVideoUrl = resolveVideoSrc(baseUrl, videoSource);
  const fallbackVideoUrl = resolveAssetSrc(baseUrl, "videos", videoSource);
  if (!primaryVideoUrl) {
    console.warn("[renderVideoContent] Could not resolve video src:", { baseUrl, videoSource });
    renderMissingMedia(section, "video", videoSource);
    return null;
  }
  const preferLocalFallback =
    !!fallbackVideoUrl &&
    fallbackVideoUrl !== primaryVideoUrl &&
    getCdnStatus().isFailing;
  const initialVideoUrl = preferLocalFallback && fallbackVideoUrl ? fallbackVideoUrl : primaryVideoUrl;
  const canFallbackToLocal = shouldAllowLocalMediaFallback(primaryVideoUrl, fallbackVideoUrl);
  // Decide whether to skip loading sources for audit mode
  const skipHeavyLoad = AUDIT_MODE && !_isFirst;
  if (DEBUG && skipHeavyLoad) {
    dbg(DEBUG, "Audit mode active → skipping video load for non-first section", { videoSource });
  }
  const sources = [initialVideoUrl];

  const video = document.createElement("video");
  video.className = "sc-media";
  const shouldLazyVideo = !_isFirst && !skipHeavyLoad;
  video.preload = shouldLazyVideo ? "none" : (skipHeavyLoad ? "none" : "metadata");
  video.setAttribute("playsinline", "true");
  video.playsInline = true;
  video.controls = false;
  video.removeAttribute("controls");

  // Allow canvas use with CDN-hosted videos (when CORS headers are present)
  try { video.crossOrigin = "anonymous"; } catch {}
  // Ensure background video never intercepts clicks/taps
  video.style.pointerEvents = "none";

  // iOS/Safari hints for reliable inline playback
  video.setAttribute("webkit-playsinline", "true");
  (video as any).webkitPlaysInline = true;
  // Optional: avoid PiP UI on hover (background videos)
  try { (video as any).disablePictureInPicture = true; } catch {}
  try { (video as any).disableRemotePlayback = true; } catch {}
  try { (video as any).controlsList?.add?.("nodownload", "nofullscreen", "noremoteplayback"); } catch {}

  const hydrateSources = () => {
    if (video.dataset.scLazyHydrated === "1") return;
    delete video.dataset.lazySrc;

    sources.forEach((src) => {
      const s = document.createElement("source");
      s.src = src;
      video.appendChild(s);
    });
    try { video.load(); } catch {}
    video.dataset.scLazyHydrated = "1";
  };

  if (!shouldLazyVideo && !skipHeavyLoad) {
    hydrateSources();
  } else if (shouldLazyVideo && !skipHeavyLoad) {
    // Defer actual src assignment until near viewport to avoid hammering the network.
    video.dataset.lazySrc = sources[0];
    try {
      const root = document.getElementById("scroll-wrapper") || undefined;
      const io = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            hydrateSources();
            obs.disconnect();
            break;
          }
        }
      }, { root, rootMargin: "120% 0px", threshold: 0.01 });
      io.observe(section);
    } catch {
      // Fallback if IO unsupported
      hydrateSources();
    }
  }

  // Safari/iOS can be picky: explicitly kick off loading after setting sources
  if (!skipHeavyLoad && !shouldLazyVideo) {
    try { video.load(); } catch {}
  }

  video.onerror = () => {
    if (canFallbackToLocal && fallbackVideoUrl && video.dataset.scUsingFallback !== "1") {
      video.dataset.scUsingFallback = "1";
      markCdnFailure(
        "CDN video failed to load; switching to local fallback",
        0,
        primaryVideoUrl
      );
      video.replaceChildren();
      const fallbackSource = document.createElement("source");
      fallbackSource.src = fallbackVideoUrl;
      video.appendChild(fallbackSource);
      try { video.load(); } catch {}
      return;
    }
    console.warn("Video failed to load:", videoSource);
    renderMissingMedia(section, "video", videoSource);
  };

  // Flags from inline attrs
  const rawAttrsStr = [effAttrs["video-v"] || effAttrs["video-h"] || "", effAttrs["video-layout"]].filter(Boolean).join(",");
  const vAttrs: Record<string, any> = parseInlineAttrs(rawAttrsStr.trim());
  dbg(DEBUG, "Parsed video attrs:", vAttrs);

  // --- autoplay/audio/loop policy (observer-friendly) ---
  const asBool = (v: any, def = false) => {
    if (v === undefined || v === null || v === "") return def;
    if (typeof v === "boolean") return v;
    const s = String(v).toLowerCase();
    return s === "1" || s === "true" || s === "yes";
  };

  const rawVideoAudio = (vAttrs["video-audio"] ?? effAttrs["video-audio"] ?? effAttrs["audio-enabled"]);
  const wantsAudio = (() => {
    if (rawVideoAudio === undefined || rawVideoAudio === null) return false;
    if (typeof rawVideoAudio === 'boolean') return rawVideoAudio;
    const s = String(rawVideoAudio).toLowerCase().trim();
    if (s === '' || s === '0' || s === 'false' || s === 'off' || s === 'mute') return false;
    return true; // any other token like 'low'/'medium'/'high' enables background audio
  })();

  const loopAttr = effAttrs["video-loop"] ?? vAttrs["video-loop"];
  const userWantsLoopOff = asBool(loopAttr, true) === false;

  // background videos are non-interactive and muted unless author explicitly enables audio
  video.muted = !wantsAudio;
  if (video.muted) video.setAttribute("muted", ""); else video.removeAttribute("muted");

  // Force background videos to wait for observer before playing
  const autoplayPref =
    vAttrs["autoplay"] ?? effAttrs["video-autoplay"] ?? effAttrs["autoplay"];
  video.dataset.autoplay = asBool(autoplayPref, true) ? "observe" : "false";
  video.autoplay = false;
  video.removeAttribute("autoplay");
  if (skipHeavyLoad) {
    // Force-disable autoplay in audit mode for non-first sections
    video.dataset.autoplay = "false";
  }

  const shouldLoop = !userWantsLoopOff;
  video.loop = shouldLoop;
  if (shouldLoop) video.setAttribute("loop", ""); else video.removeAttribute("loop");
  // Expose loop intent for other systems (e.g., unlock/observer)
  video.dataset.loop = shouldLoop ? "true" : "false";

  // Optional author flag to rewind on leave
  if (asBool(vAttrs["reset-on-leave"] ?? effAttrs["reset-on-leave"], false)) {
    video.dataset.resetOnLeave = "true";
  }

  // Helpful hint for other systems/analytics
  video.dataset.role = "background";
  video.dataset.bg = "true";
  if (skipHeavyLoad) {
    video.dataset.auditSkip = "true";
  }
  // --- propagate audio-level setting for mediaUnlock.ts ---
  if (effAttrs["video-audio"]) {
    video.dataset.videoAudio = String(effAttrs["video-audio"]).toLowerCase();
  }

  // 5) Stage stack: propagate author intent for downstream utilities before appending video
  // Pass through author intent for future systems (mediaUnlock, etc.)
  if (effAttrs["video-loop"]) {
    video.dataset.loop = String(effAttrs["video-loop"]);
  }
  if (effAttrs["video-crop"]) {
    video.dataset.videoCrop = String(effAttrs["video-crop"]);
  }
  stageEl.appendChild(video);

  // Keep the reel frame synced to the video’s visual rect inside the section
  const syncReelFrameToVideo = () => {
    try {
      if (!reelFrame) return;
      const secRect = section.getBoundingClientRect();
      const vRect = video.getBoundingClientRect();
      // Translate viewport coords to section-local absolute coords
      const left = Math.max(0, vRect.left - secRect.left);
      const top = Math.max(0, vRect.top - secRect.top);
      const width = Math.max(0, vRect.width);
      const height = Math.max(0, vRect.height);
      Object.assign(reelFrame.style, {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
      } as Partial<CSSStyleDeclaration>);
    } catch {}
  };

  let scContent = stageEl.querySelector(':scope > .sc-content') as HTMLDivElement | null;
  if (!scContent) {
    scContent = document.createElement("div");
    scContent.className = "sc-content";
    stageEl.appendChild(scContent);
  }
  Object.assign(scContent.style, {
    position: "absolute",
    left: "0",
    top: "0",
    width: "100%",
    height: "100%",
    zIndex: String(videoZ("content")),
  });

  // Move any existing top-level .container into sc-content
  const moveContainerIntoStage = () => {
    const existing = section.querySelector(':scope > .container') as HTMLElement | null;
    if (existing && existing.parentElement !== scContent) {
      scContent!.appendChild(existing);
      // Reel-specific: trim default padding so text aligns closer to the gutter split.
      if (isReel) {
        try {
          existing.style.setProperty("--padding-h", "1rem");
          existing.style.setProperty("--padding-v", "1rem");
        } catch {}
      }
    }
  };
  moveContainerIntoStage();

  // Adopt containers that show up later (markdown/render timing)
  const adoptMO = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const n of Array.from(m.addedNodes)) {
        if (n instanceof HTMLElement && n.matches(".container") && n.parentElement === section) {
          scContent!.appendChild(n);
        }
      }
    }
  });
  adoptMO.observe(section, { childList: true });

  // Auto-disconnect if section removed
  const cleanupMO = new MutationObserver(() => {
    if (!document.body.contains(section)) {
      try { adoptMO.disconnect(); } catch {}
      try { cleanupMO.disconnect(); } catch {}
      window.removeEventListener("resize", onResize);
      try { ro?.disconnect(); ro = null; } catch {}
    }
  });
  cleanupMO.observe(document.body, { childList: true, subtree: true });

  // 6) Defer geometry/blur to layout pipeline when metadata is ready
  video.addEventListener("loadedmetadata", () => {
    const isVerticalScreen = window.innerHeight > window.innerWidth;
    const isVerticalVideo = video.videoHeight > video.videoWidth;
    dbg(DEBUG, "Metadata:", {
      w: video.videoWidth,
      h: video.videoHeight,
      isVerticalScreen,
      isVerticalVideo,
    });

    // Loop policy re-assert (in case attrs changed/parsed late)
    video.loop = shouldLoop;
    if (shouldLoop) video.setAttribute("loop", "");
    else video.removeAttribute("loop");
    video.dataset.loop = shouldLoop ? "true" : "false";

    // Ensure video-crop and video-audio survive into layout utils
    if (video.dataset.videoCrop && !effAttrs["video-crop"]) {
      effAttrs["video-crop"] = video.dataset.videoCrop;
    }
    if (video.dataset.videoAudio && !effAttrs["video-audio"]) {
      effAttrs["video-audio"] = video.dataset.videoAudio;
    }
    // Apply full layout (this will also set up/refresh the blur canvas)
    const res = applyVideoLayout(section, video, effAttrs);

    // Ensure any created canvas lives inside stage for correct z-order
    // (setupVideoCanvas appends to section; we tuck it into the stage)
    const canv = section.querySelector("canvas.sc-canvas") as HTMLCanvasElement | null;
    if (canv && canv.parentElement !== stageEl) {
      stageEl.appendChild(canv);
      // Keep the canvas beneath content
      canv.style.zIndex = String(videoZ("blur"));
      canv.style.position = "absolute";
      (canv.style as any).inset = "0";
      canv.style.pointerEvents = "none";
    }
    if (canv) {
      canv.dataset.role = "video-blur";
    }

    // Align the reel frame to the video window
    syncReelFrameToVideo();

    // --- overlay (insert into .sc-content above video/blur, below text) ---
    try {
      const overlayKey = String(effAttrs["overlay"] || "").toLowerCase();
      if (overlayKey && overlayClassMap[overlayKey]) {
        // Remove any previous overlays for idempotency
        scContent.querySelectorAll(".sc-overlay").forEach((el) => el.remove());

        const overlayDiv = document.createElement("div");
        overlayDiv.className = `sc-overlay absolute inset-0 pointer-events-none ${overlayClassMap[overlayKey]}`;
        overlayDiv.style.zIndex = String(videoZ("gradient")); // sits under content, above video/blur

        // Insert before the actual content container if present
        const container = scContent.querySelector(".container");
        if (container && container.parentElement === scContent) {
          scContent.insertBefore(overlayDiv, container);
        } else {
          scContent.appendChild(overlayDiv);
        }
      }
    } catch (e) {
      console.warn("[video.ts] Overlay injection failed:", e);
    }

    // --- Tag and enforce reel play-once policy at creation time ---
    const isReel = String(effAttrs['is-reel'] || '').toLowerCase() === '1';
    if (isReel) {
      try {
        // Tag this as a reel so observers/HUD know the intent
        video.dataset.reel = "true";
        // Enforce play-once for reels at the element level
        video.loop = false;
        video.removeAttribute("loop");
        video.dataset.reelPlayOnce = "true";
        // Mark role for downstream logic and reflect loop intent explicitly
        video.dataset.role = "reel";
        video.dataset.loop = "false";
        instrumentReelVideo(video, section);
      } catch {}
    }

    // --- Reel HUD (mount inside the stage container when authored as a reel) ---
    try {
      if (isReel) {
        // Remove any existing narration HUDs before mounting reel HUD
        section.querySelectorAll('.lite-hud-controls.narration-hud').forEach(el => el.remove());

        // Avoid double-mount if something already inserted a HUD here
        const already = (reelFrame && reelFrame.querySelector('.lite-hud-controls, .reel-hud'))
                        || stageEl.querySelector('.lite-hud-controls, .reel-hud');
        if (!already && reelFrame) {
          mountReelHUD({ section, reelFrame, video, isFirstReel: false });
        }
      }
    } catch (e) {
      console.warn("[renderVideoContent] mountReelHUD failed:", e);
    }

    // --- Captions overlay (mounted inside stage so it follows reel/video frame) ---
    try {
      // Skip captions if the author explicitly used directional video fields in original attrs.
      // Use the raw `attrs` (not `effAttrs`) so that `video-reel` aliasing to `video-v` does NOT suppress captions.
      const authoredDirectional = !!(attrs && (attrs["video-h"] || attrs["video-v"]));

      // Reels mount captions through the reel HUD path. Mounting a second
      // captions handle here can desync the CC button from the visible layer.
      if (!isReel && !authoredDirectional) {
        // Only attempt captions if author provided a track or an SRT is discoverable.
        // mountCaptionsElement will internally check availability and user prefs.
        mountCaptionsElement({
          section,
          reelFrame: reelFrame ?? stageEl, // anchor captions to the video window when possible
          video,
          attrs: effAttrs,
          baseUrl
        });
      } else if (!isReel && DEBUG) {
        dbg(DEBUG, "Skipping captions for directional video", { videoSource });
      }
    } catch (e) {
      console.warn("[renderVideoContent] mountCaptionsElement failed:", e);
    }

    dbg(DEBUG, "Layout applied:", res);
  }, { once: true });

  // Keep layout responsive
  const onResize = () => {
    if (video.videoWidth && video.videoHeight) {
      // Re-apply layout on resize to honor new stage bounds/orientation
      const res = applyVideoLayout(section, video, effAttrs);
      const canv = section.querySelector("canvas.sc-canvas") as HTMLCanvasElement | null;
      if (canv && canv.parentElement !== stageEl) {
        stageEl.appendChild(canv);
        canv.style.zIndex = String(videoZ("blur"));
        canv.style.position = "absolute";
        (canv.style as any).inset = "0";
        canv.style.pointerEvents = "none";
      }
      syncReelFrameToVideo();
      dbg(DEBUG, "Layout re-applied (resize):", res);
    }
  };
  window.addEventListener("resize", onResize);
  // keep a handle so we can disconnect later
  let ro: ResizeObserver | null = null;
  try {
    ro = new ResizeObserver(onResize);
    ro.observe(stageEl);
    syncReelFrameToVideo();
  } catch {}

  return video;
}
